import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IconCopy,
  IconEdit,
  IconEye,
  IconSearchOff,
  IconTrash,
  IconUser,
} from '@tabler/icons-react';

import { AgentProfileResponseDto } from '@shared/services/apiService';
import { useStyles } from '../styles';
import { RootState, useAppSelector } from '@shared/store';
import { useDesignVariant } from '@shared/designVariants/DesignVariantProvider';
import { UserRolesEnum } from '@shared/constants/auth.constants';

interface AgentProfileListProps {
  profiles: AgentProfileResponseDto[];
  searchQuery?: string;
  totalCount?: number;
  onEdit: (profile: AgentProfileResponseDto) => void;
  onView: (profile: AgentProfileResponseDto) => void;
  onCopy: (profile: AgentProfileResponseDto) => void;
  onDelete: (profile: AgentProfileResponseDto) => void;
  onUse?: (profile: AgentProfileResponseDto) => void;
}

export const AgentProfileList: React.FC<AgentProfileListProps> = ({
  profiles,
  searchQuery = '',
  totalCount = 0,
  onEdit,
  onCopy,
  onView,
  onDelete,
  onUse,
}) => {
  const { variant } = useDesignVariant();
  const rows = ['v3', 'v4', 'v5'].includes(variant);
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const currentUserRole = useAppSelector((state: RootState) => state.auth.authenticatedUser?.role);
  const hasActiveSearch = searchQuery.trim().length > 0;
  const isViewer = currentUserRole === UserRolesEnum.VIEWER;

  if (totalCount === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" color="text.secondary">
          {t('assistantsPage.emptyState.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('assistantsPage.emptyState.description')}
        </Typography>
      </Box>
    );
  }

  if (profiles.length === 0 && hasActiveSearch) {
    return (
      <Box className={classes.emptyState}>
        <IconSearchOff size={48} className={classes.emptyStateIcon} />
        <Typography variant="h6" color="text.secondary">
          {t('assistantsPage.emptyState.noSearchResults.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('assistantsPage.emptyState.noSearchResults.description')}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {profiles.map((profile) => (
        <Grid item xs={12} md={rows ? 12 : 6} lg={rows ? 12 : 4} key={profile.id}>
          <Card className={classes.profileCard}>
            <CardContent className={cx(classes.profileCardInner, rows && classes.profileRow)}>
              <Box className={classes.profileCardTop}>
                <Avatar className={classes.profileAvatar} aria-hidden>
                  <IconUser size={20} stroke={1.5} />
                </Avatar>
                <Typography className={classes.profileName} title={profile.name}>
                  {profile.name}
                </Typography>
              </Box>

              {profile.prompt ? (
                <Typography className={classes.profilePrompt}>{profile.prompt}</Typography>
              ) : (
                <Typography className={classes.profilePromptEmpty}>
                  {t('assistantsPage.modals.view.noPrompt')}
                </Typography>
              )}

              {profile.firstMessage?.trim() ? (
                <Box className={classes.profileMeta}>
                  <Chip
                    label={t('assistantsPage.card.openingLine')}
                    size="small"
                    variant="outlined"
                    className={classes.openingLineChip}
                  />
                </Box>
              ) : null}

              <Divider className={classes.profileCardDivider} />

              <Box className={classes.profileCardActions}>
                {onUse && (
                  <Button size="small" disabled={isViewer} onClick={() => onUse(profile)}>
                    Use this job
                  </Button>
                )}
                <Box className={classes.actionGroup}>
                  <Tooltip title={t('assistantsPage.actions.view')} arrow placement="top">
                    <IconButton
                      data-testid="agent-profile-view"
                      size="small"
                      onClick={() => onView(profile)}
                      className={classes.actionButton}
                      aria-label={t('assistantsPage.actions.view')}>
                      <IconEye size={17} stroke={1.5} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('assistantsPage.actions.copy')} arrow placement="top">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onCopy(profile)}
                        className={classes.actionButton}
                        disabled={isViewer || !profile.prompt}
                        aria-label={t('assistantsPage.actions.copy')}>
                        <IconCopy size={17} stroke={1.5} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('assistantsPage.actions.edit')} arrow placement="top">
                    <span>
                      <IconButton
                        data-testid="agent-profile-edit"
                        size="small"
                        onClick={() => onEdit(profile)}
                        className={classes.actionButton}
                        disabled={isViewer || profile.type !== 'custom'}
                        aria-label={t('assistantsPage.actions.edit')}>
                        <IconEdit size={17} stroke={1.5} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                <Tooltip title={t('assistantsPage.actions.delete')} arrow placement="top">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(profile)}
                      className={cx(classes.actionButton, classes.actionButtonDelete)}
                      disabled={isViewer || profile.type !== 'custom'}
                      aria-label={t('assistantsPage.actions.delete')}>
                      <IconTrash size={17} stroke={1.5} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};
